import React from "react";
import { useNavigate } from "react-router-dom";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";
import PageHero from "./PageHero";
import SDLCInfographic from "./SDLCInfographic";
import { Helmet } from "react-helmet-async";
import {
  Zap,
  Monitor,
  Code,
  Target,
  MessageCircle,
  Layers,
  PenTool,
  Shield,
  TrendingUp,
  Search,
  RotateCw,
  Users,
  Palette,
  Layout,
  Briefcase,
  Workflow,
  GitBranch,
  BarChart2,
  Bell,
  Cpu,
  Database,
  Cloud,
  Terminal,
  Smartphone,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const StrategyAndProcess = () => {
  const navigate = useNavigate();

  // Content for Software Architecture & Strategy
  const strategyPoints = [
    {
      icon: <Cpu className="w-6 h-6 text-white" />,
      title: "System Design & Architecture",
      detail:
        "Designing scalable, high-availability system architectures tailored to business needs.",
    },
    {
      icon: <Database className="w-6 h-6 text-white" />,
      title: "Database Modeling & API Strategy",
      detail:
        "Optimizing data structures and designing robust REST/GraphQL APIs for seamless integration.",
    },
    {
      icon: <Terminal className="w-6 h-6 text-white" />,
      title: "Tech Stack Selection",
      detail:
        "Choosing the right tools and frameworks to ensure long-term maintainability and performance.",
    },
    {
      icon: <Cloud className="w-6 h-6 text-white" />,
      title: "Cloud & Infrastructure Planning",
      detail:
        "Strategizing AWS/Azure/GCP deployments with focus on security, cost, and scalability.",
    },
  ];

  // Content for Development Process (Agile)
  const processPoints = [
    {
      icon: <Search className="w-6 h-6 text-white" />,
      title: "Discovery & Requirements",
      detail: "Deep analysis to define functional requirements and technical scope.",
    },
    {
      icon: <Workflow className="w-6 h-6 text-white" />,
      title: "Sprint Planning & Backlog",
      detail: "Breaking down complex features into manageable, prioritized tasks.",
    },
    {
      icon: <Code className="w-6 h-6 text-white" />,
      title: "Iterative Development",
      detail: "Regular coding sprints with continuous integration and internal testing.",
    },
    {
      icon: <Bell className="w-6 h-6 text-white" />,
      title: "QA & User Acceptance",
      detail: "Rigorous testing phases to ensure stability and user satisfaction.",
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-white" />,
      title: "Deployment & Optimization",
      detail:
        "Production launch followed by performance monitoring and iterative improvements.",
    },
  ];

  // Content for Software Delivery Models
  const deliveryPoints = [
    {
      icon: <Briefcase className="w-6 h-6 text-white" />,
      title: "Enterprise Solutions",
      detail:
        "Large-scale software systems designed for complex organizational workflows.",
    },
    {
      icon: <Zap className="w-6 h-6 text-white" />,
      title: "Startup MVPs",
      detail:
        "Rapid development of core features to validate product-market fit quickly.",
    },
  ];

  // User Experience Pillars
  const visualStorytellingPillars = [
    {
      icon: <Palette className="w-6 h-6 text-white" />,
      title: "Intuitive UI Design",
      detail:
        "Creating interfaces that are visually appealing and easy for users to navigate.",
    },
    {
      icon: <Smartphone className="w-6 h-6 text-white" />,
      title: "Responsive & Mobile-First",
      detail:
        "Ensuring seamless performance and layout across all devices and screen sizes.",
    },
    {
      icon: <Users className="w-6 h-6 text-white" />,
      title: "Accessibility Standards",
      detail:
        "Building inclusive software that follows WCAG guidelines for all user types.",
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-white" />,
      title: "Performance Optimization",
      detail:
        "Tuning front-end assets and interactions for lightning-fast load times.",
    },
  ];

  // --- Supporting Grid Data ---

  const strategicFoundations = [
    {
      icon: <Target className="w-6 h-6 text-orange-600" />,
      title: "Goal-Oriented Design",
      detail:
        "Every technical decision is aligned with defined business KPIs and user needs.",
    },
    {
      icon: <Layers className="w-6 h-6 text-orange-600" />,
      title: "Modular Codebase",
      detail:
        "Building with microservices or modular monoliths to ensure future flexibility.",
    },
    {
      icon: <Shield className="w-6 h-6 text-orange-600" />,
      title: "Security by Design",
      detail:
        "Implementing security best practices at every layer of the application stack.",
    },
    {
      icon: <RotateCw className="w-6 h-6 text-orange-600" />,
      title: "Continuous Feedback",
      detail:
        "Regular stakeholder reviews ensure the product evolves in the right direction.",
    },
  ];

  const creativeDeliverables = [
    {
      icon: <PenTool className="w-6 h-6 text-orange-600" />,
      title: "Detailed Documentation",
      detail:
        "Comprehensive API docs, system architecture diagrams, and user manuals.",
    },
    {
      icon: <Monitor className="w-6 h-6 text-orange-600" />,
      title: "Interactive Prototypes",
      detail:
        "High-fidelity wireframes and clickable prototypes to visualize the end product.",
    },
    {
      icon: <Zap className="w-6 h-6 text-orange-600" />,
      title: "Deployment Pipelines",
      detail:
        "Automated CI/CD workflows for reliable and frequent production releases.",
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-orange-600" />,
      title: "Technical Consultation",
      detail:
        "Expert advice on digital transformation and modernizing legacy systems.",
    },
  ];

  const performanceAssurance = [
    {
      icon: <TrendingUp className="w-6 h-6 text-orange-600" />,
      title: "Load & Stress Testing",
      detail:
        "Simulating high traffic to ensure system stability under extreme conditions.",
    },
    {
      icon: <Shield className="w-6 h-6 text-orange-600" />,
      title: "Automated QA Suites",
      detail:
        "Unit, integration, and E2E tests to prevent regressions and ensure quality.",
    },
    {
      icon: <Monitor className="w-6 h-6 text-orange-600" />,
      title: "Real-time Error Tracking",
      detail:
        "Proactive monitoring with tools like Sentry or LogRocket for rapid issue resolution.",
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-orange-600" />,
      title: "Usage Analytics",
      detail:
        "Tracking user behavior to drive data-informed feature enhancements.",
    },
  ];

  // Helper functions
  const hexagonClipPath =
    "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";
  const HexagonIcon = ({ children, color }) => (
    <div
      className={`w-14 h-14 flex items-center justify-center p-1 shadow-lg`}
      style={{
        clipPath: hexagonClipPath,
        background: color,
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="relative min-h-screen bg-gray-50 overflow-hidden font-sans">
      <Helmet>
        <title>Strategy & Architecture | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Our systematic engineering approach ensures technical concepts translate into robust, scalable, and high-performance digital products." />
        <meta property="og:title" content="Strategy & Architecture | Winsil Evolution Pvt Ltd" />
        <meta property="og:description" content="Explore our system design, database modeling, and agile development process." />
      </Helmet>
      <PortfolioNavbar />

      <PageHero
        label="How We Work"
        labelClass="text-orange-400"
        title="Software Strategy & Execution Blueprint"
        subtitle="Our systematic engineering approach ensures technical concepts translate into robust, scalable, and high-performance digital products."
        image="/images/photo-1517180102446-f3ece451e9d8.jpg"
        imageAlt="Code and architecture planning"
        bgClass="bg-gray-950"
        glowColor="#f97316"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center mb-2 text-orange-500">Our Engineering Approach</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Strategy Meets Execution</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1531403009284-440f080d1e12.jpg", alt: "Architecture planning session", label: "System Design" },
              { src: "/images/photo-1552664730-d307ca884978.jpg", alt: "Tech planning whiteboard", label: "Tech Planning" },
              { src: "/images/photo-1573164713988-8665fc963095.jpg", alt: "Engineering team meeting", label: "Agile Sprints" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1461749280684-dccba630e2f6.jpg", alt: "Code architecture review", label: "Code Architecture" },
              { src: "/images/photo-1517180102446-f3ece451e9d8.jpg", alt: "Cloud deployment", label: "Cloud Deployment" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="bg-gray-900 border-y border-white/10 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "100+", label: "Systems Architected" },
            { value: "2hr",  label: "CI/CD Setup Time" },
            { value: "99.9%", label: "Uptime SLA" },
            { value: "0",    label: "Zero-Downtime Deploys" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black text-orange-400 mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* --- Main Content Container --- */}
      <div className="relative max-w-7xl mx-auto pt-12 pb-12 px-4 sm:px-6 lg:px-8 z-10">

        {/* --- 1. CORE PILLARS: STRATEGY & VISUALS (Main Card) --- */}
        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-orange-500 mb-16">
          <div className="flex flex-col lg:flex-row mb-16 lg:space-x-12">
            <div className="w-full lg:w-1/2 mt-10 lg:mt-0 flex flex-col justify-start order-1 lg:order-2 mb-8 lg:mb-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <HexagonIcon color="linear-gradient(135deg, #FF69B4, #8A2BE2)">
                  <Code className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-3xl font-bold text-orange-600">
                  Engineering Strategy
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {strategyPoints.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-1/2 mt-10 lg:mt-0 flex flex-col justify-start order-1 lg:order-2 mb-8 lg:mb-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-8">
                <HexagonIcon color="linear-gradient(45deg, #ff8c00, #ff4500)">
                  <Layout className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-3xl font-bold text-orange-600">
                  User Experience Pillars
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {visualStorytellingPillars.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <hr className="my-10 border-t-2 border-gray-100" />

          <div className="flex flex-col-reverse lg:flex-row lg:space-x-12">
            <div className="w-full lg:w-1/2 p-6 lg:p-0 order-1 mb-10 lg:mb-0">
              <div className="p-4 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-500 h-full">
                <div className="flex items-center space-x-4 mb-6">
                  <HexagonIcon color="linear-gradient(-45deg, #8b0000, #ff6347)">
                    <GitBranch className="w-6 h-6 text-white" />
                  </HexagonIcon>
                  <h3 className="text-3xl font-bold text-orange-600">
                    Software Delivery Models
                  </h3>
                </div>
                <p className="text-lg text-gray-800 mb-6">
                  We offer flexible delivery models tailored to your specific project needs, 
                  ensuring optimal resource allocation and project success.
                </p>
                <div className="grid grid-cols-1 gap-6">
                  {deliveryPoints.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                          {item.title}
                        </h4>
                        <p className="text-gray-700 text-sm">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/2 p-6 lg:p-0 order-2">
              <div className="p-4 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
                <div className="flex items-center space-x-4 mb-6">
                  <HexagonIcon color="linear-gradient(45deg, #ff8c00, #ff4500)">
                    <Workflow className="w-6 h-6 text-white" />
                  </HexagonIcon>
                  <h3 className="text-3xl font-bold text-orange-600">
                    Agile Development Process
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {processPoints.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 p-2 bg-orange-600 rounded-lg h-10 w-10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                          {item.title}
                        </h4>
                        <p className="text-gray-700 text-sm">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- 2. STRATEGIC FOUNDATIONS (4 SECTIONS - GRID) --- */}
        <div className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50 rounded-xl shadow-inner mb-16">
          <h2 className="text-4xl font-extrabold text-orange-600 mb-12 text-center">
            Strategic Foundations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {strategicFoundations.map((item, index) => (
              <div
                key={index}
                className="flex space-x-4 p-4 bg-white rounded-lg shadow-lg border-t-4 border-orange-500"
              >
                <div className="flex-shrink-0 p-2 border-2 border-orange-500 rounded-lg h-12">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-700 text-sm">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- 3. ENGINEERING DELIVERABLES (4 SECTIONS - GRID) --- */}
        <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white rounded-xl shadow-xl mb-16">
          <h2 className="text-4xl font-extrabold text-orange-600 mb-12 text-center">
            Engineering Deliverables
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {creativeDeliverables.map((item, index) => (
              <div
                key={index}
                className="text-center p-6 border-b-4 border-orange-500 rounded-lg shadow-md"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-orange-100 rounded-full">{item.icon}</div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">
                  {item.title}
                </h4>
                <p className="text-gray-700 text-sm">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* --- 4. PERFORMANCE ASSURANCE (4 SECTIONS - GRID) --- */}
        <div className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50 rounded-xl shadow-inner">
          <h2 className="text-4xl font-extrabold text-orange-600 mb-12 text-center">
            Quality & Performance Assurance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {performanceAssurance.map((item, index) => (
              <div
                key={index}
                className="flex space-x-4 p-4 bg-white rounded-lg shadow-lg border-l-4 border-orange-600"
              >
                <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg h-12">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-700 text-sm">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- SDLC Visual Blueprint --- */}
      <section className="bg-white border-t-4 border-orange-500">
        <h2 className="text-4xl font-extrabold text-gray-900 pt-16 text-center">
          Our Software Development Life Cycle
        </h2>
        <SDLCInfographic />
      </section>

      {/* ── Why Winsil ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group order-2 lg:order-1">
            <img
              src="/images/photo-1542744094-24638eff58bb.jpg"
              alt="Software architecture planning"
              className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-orange-600">99.9%</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Uptime SLA</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Our Advantage</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
              Architecture Built to <span className="text-orange-500">Scale</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              We treat architecture as a first-class concern. Before writing a single line of code,
              we map system boundaries, data flows, and failure modes — so your platform is ready
              to handle 10× traffic on day one.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "Architecture reviews before every project kick-off",
                "Infrastructure as Code with Terraform for reproducibility",
                "Automated rollback on failed deployments",
                "Security threat modeling built into the design phase",
                "Load testing before every major release",
              ].map((item) => (
                <li key={item} className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/25"
            >
              Discuss Your Architecture <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gray-950 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-amber-400" />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-400 mb-4">Start Planning</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Let's Design Your Technical Blueprint</h2>
          <p className="text-white/55 text-lg mb-10 max-w-xl mx-auto">Share your project goals and we'll produce a technical architecture proposal within 5 business days.</p>
          <button
            onClick={() => navigate("/contact")}
            className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get a Free Architecture Review <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      {/* --- Footer Integration --- */}
      <PortfolioFooter />
    </div>
  );
};

export default StrategyAndProcess;
